import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Mail, MapPin, Phone, ExternalLink } from "lucide-react";

const LAST_UPDATED = "2 April 2026";

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-br from-teal-700 to-teal-800 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-teal-100 hover:text-white transition-colors mb-4 text-sm"
            data-testid="privacy-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to website
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <Shield className="h-8 w-8 text-orange-400" />
            <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-teal-100">
            How Barnston Court Nursing Home collects, uses and protects your personal data.
          </p>
          <p className="text-teal-200 text-sm mt-3">Last updated: {LAST_UPDATED}</p>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 prose prose-teal max-w-none">
        <section>
          <p>
            <strong>Barnston Court Nursing Home</strong> is operated by{" "}
            <strong>Cozee Care Homes Ltd</strong>, the registered data controller.
            Our nursing home is located at{" "}
            <strong>21 Barnston Lane, Moreton, Wirral, Merseyside, CH46 7TN</strong>{" "}
            (in this policy, &ldquo;we&rdquo;, &ldquo;our&rdquo; or &ldquo;us&rdquo;
            refers to Cozee Care Homes Ltd operating as Barnston Court Nursing Home).
            We are registered with the Information Commissioner&apos;s Office under
            registration number{" "}
            <strong>
              <a
                href="https://ico.org.uk/ESDWebPages/Entry/ZA470038"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-700 underline hover:text-teal-900"
              >
                ZA470038
              </a>
            </strong>
            . We are committed to ensuring that your privacy is protected. We comply
            with the UK General Data Protection Regulation (UK GDPR), the Data
            Protection Act 2018 and any other relevant data protection and privacy
            legislation (together, &ldquo;Data Protection Legislation&rdquo;).
          </p>
          <p>
            This Privacy Policy &mdash; together with our{" "}
            <Link to="/cookie-policy" className="text-teal-700 underline hover:text-teal-900">
              Cookie Policy
            </Link>{" "}
            &mdash; sets out how we collect personal information from you and how the personal
            information you provide will be processed by us. By visiting{" "}
            <a href="https://barnstoncourt.com" className="text-teal-700 underline hover:text-teal-900">
              www.barnstoncourt.com
            </a>{" "}
            (the &ldquo;Website&rdquo;) you are accepting and consenting to the practices
            described in this Privacy Policy. If you do not consent, please do not submit any
            personal data to us.
          </p>
        </section>

        <h2>What information do we hold and how do we use it?</h2>

        <h3>Information you give us</h3>
        <p>
          You may give us information about you when you submit one of the forms on the
          Website. The information collected through our forms includes:
        </p>
        <ul>
          <li><strong>Enquiry &amp; visit booking forms</strong> &mdash; your name, phone number, email address, care type of interest, urgency level and any message you provide.</li>
          <li><strong>Job application form</strong> &mdash; your name, phone number, email address, position applied for, relevant experience, optional cover message and (optionally) your CV / résumé file.</li>
          <li><strong>Review submission form</strong> &mdash; your name, email address, relationship to the resident, type of care received, length of stay and the text of your review.</li>
          <li><strong>Complaint form</strong> &mdash; your name, contact details and the details of your complaint.</li>
        </ul>
        <p>
          We will retain this information for as long as it is necessary to correspond with
          you, deliver the service you have requested, comply with our legal and regulatory
          obligations (including Care Quality Commission record-keeping requirements) and the
          retention periods set out in the Records Management Code of Practice for Health and
          Social Care. Job application data including CVs is retained for up to 12 months
          after the recruitment decision unless you ask us to delete it sooner.
        </p>

        <h3>Information we collect about you automatically</h3>
        <p>
          When you visit the Website we may collect:
        </p>
        <ul>
          <li>Technical information including the IP address used to connect your device to the internet, browser type and version, time-zone setting, operating system and platform.</li>
          <li>Information about your visit, including the pages you viewed, the date and time, page response times, errors, length of visits and page-interaction information.</li>
        </ul>
        <p>
          This data is collected through cookies and similar technologies; see our{" "}
          <Link to="/cookie-policy" className="text-teal-700 underline hover:text-teal-900">Cookie Policy</Link>{" "}
          for full details. We retain analytical data for up to 12 months.
        </p>

        <h2>Cookies</h2>
        <p>
          The Website uses cookies to distinguish you from other users of the Website. For
          detailed information on the cookies we use and the purposes for which we use them,
          please see our{" "}
          <Link to="/cookie-policy" className="text-teal-700 underline hover:text-teal-900">Cookie Policy</Link>.
        </p>

        <h2>How we use your information</h2>
        <p>We use the information we receive and/or collect about you to:</p>
        <ul>
          <li>Respond to your enquiries and arrange visits, brochure requests or callbacks.</li>
          <li>Process job applications, contact you about recruitment and consider you for current or future opportunities.</li>
          <li>Review and (where appropriate) publish your testimonial, after moderation, on our Reviews page.</li>
          <li>Investigate and respond to complaints.</li>
          <li>Provide care services and fulfil our obligations under any contract entered into between us.</li>
          <li>Monitor Website usage so we can improve the Website and the services we provide via it.</li>
          <li>Comply with our legal, regulatory and CQC obligations.</li>
        </ul>
        <p>
          We process personal data on the following lawful bases under UK GDPR Article 6:
          your consent (where you have given it), the performance of a contract or steps
          taken at your request prior to entering a contract, compliance with a legal
          obligation, and our legitimate interests in operating, improving and securing the
          Website and our service.
        </p>
        <p>
          We will not share, sell or distribute any of the information you provide to us
          (other than as set out in this policy) without your prior consent, unless required
          to do so by law.
        </p>
        <p>
          We do not carry out any automated decision-making (including profiling) that
          produces legal or similarly significant effects on you.
        </p>

        <h2>Third parties &amp; how we share your data</h2>
        <p>
          We share your personal data only with carefully selected third parties who help us
          operate the Website and our service:
        </p>
        <ul>
          <li><strong>SendGrid (Twilio Inc.)</strong> &mdash; to transmit notification and reply emails relating to your enquiry or application.</li>
          <li><strong>MongoDB</strong> &mdash; secure hosted database where your submissions are stored.</li>
          <li><strong>PostHog</strong> &mdash; product analytics provider that helps us understand how visitors use the Website (see our Cookie Policy).</li>
          <li><strong>Google Maps</strong> &mdash; embedded map showing the location of our home.</li>
          <li><strong>Our hosting and infrastructure providers</strong>, who host the Website on our behalf.</li>
        </ul>
        <p>
          We may also disclose your personal information where we are under a legal duty to
          do so, where it is necessary to protect our rights, property or the safety of our
          residents, staff or others, or in the event of a sale, merger or restructure of
          our business.
        </p>

        <h2>Third-party websites</h2>
        <p>
          Our Website may contain links to third-party websites (such as the Care Quality
          Commission, carehome.co.uk, our parent organisation Cozee Care, and our Facebook
          page). These links are provided for convenience only and we do not endorse or
          control the content or privacy practices of those websites. Please review the
          privacy policy of any third-party website before submitting personal data to it.
        </p>

        <h2>How safe is your information?</h2>
        <p>
          Protecting your security and privacy is important to us. The Website is served
          over HTTPS and your submissions are stored in an access-controlled database.
          Reasonable technical and organisational measures are in place to protect your
          personal data from unauthorised or unlawful access, processing, accidental loss,
          destruction or damage.
        </p>
        <p>
          However, the transmission of information via the internet is not completely
          secure. Although we will do our best to protect your personal data, any
          transmission is at your own risk.
        </p>

        <h2>Where we store your personal data</h2>
        <p>
          All your personal data is stored within the UK or the European Economic Area
          (EEA). Where any of our service providers (such as SendGrid or PostHog) may
          transfer data outside the UK/EEA, they do so only under appropriate safeguards
          recognised by the UK GDPR (such as the UK International Data Transfer Agreement
          or Standard Contractual Clauses).
        </p>

        <h2>Your rights in respect of your data</h2>
        <p>
          You have a number of rights in respect of the personal data we process about you.
          More information is available from the Information Commissioner&apos;s Office
          (ICO). In certain limited circumstances some of these rights may not be available
          to you; if so, we will let you know when you make a request. Your rights are:
        </p>
        <ol>
          <li><strong>Right of access</strong> &mdash; to obtain a copy of the personal data we hold about you.</li>
          <li><strong>Right to rectification</strong> &mdash; to ask us to correct inaccurate or incomplete data.</li>
          <li><strong>Right to erasure</strong> (&ldquo;right to be forgotten&rdquo;) &mdash; to ask us to delete your data in certain circumstances.</li>
          <li><strong>Right to restrict processing</strong> &mdash; to ask us to limit how we use your data.</li>
          <li><strong>Right to data portability</strong> &mdash; to receive your data in a structured, machine-readable format.</li>
          <li><strong>Right to object</strong> &mdash; to object to processing carried out on the basis of legitimate interests, or for direct-marketing purposes.</li>
          <li><strong>Right to withdraw consent</strong> &mdash; where we rely on your consent, you can withdraw it at any time.</li>
        </ol>
        <p>
          To exercise any of these rights, please contact us using the details below.
        </p>

        <h2>How to complain</h2>
        <p>
          If you have any complaint about our use of your personal data, please contact us
          first using the details below. We will acknowledge your complaint within 14 days
          of receipt and respond in full without undue delay, and in any event within 30
          days. If the matter is complex, we may extend this period by up to a further 30
          days and will keep you informed.
        </p>
        <p>
          You also have the right to lodge a complaint with the Information
          Commissioner&apos;s Office (ICO) at any time, with or without first contacting us:
        </p>
        <ul>
          <li>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline hover:text-teal-900">ico.org.uk <ExternalLink className="inline h-3 w-3" /></a></li>
          <li>Telephone: 0303 123 1113</li>
          <li>Post: Information Commissioner&apos;s Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</li>
        </ul>

        <h2>Contact us</h2>
        <div className="not-prose bg-teal-50 border border-teal-200 rounded-lg p-6 mt-4">
          <p className="font-semibold text-gray-900 mb-1">Cozee Care Homes Ltd</p>
          <p className="text-sm text-gray-600 mb-3">operating as Barnston Court Nursing Home</p>
          <ul className="space-y-2 text-gray-700 text-sm">
            <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" /> 21 Barnston Lane, Moreton, Wirral, Merseyside, CH46 7TN</li>
            <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-teal-600 flex-shrink-0" /> <a href="tel:01516775573" className="text-teal-700 hover:underline">0151 677 5573</a></li>
            <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-teal-600 flex-shrink-0" /> <a href="mailto:manager@barnstoncourt.com" className="text-teal-700 hover:underline">manager@barnstoncourt.com</a></li>
            <li className="flex items-center gap-2"><Shield className="h-4 w-4 text-teal-600 flex-shrink-0" /> ICO Registration: <strong>ZA470038</strong></li>
          </ul>
        </div>

        <h2>Changes to this policy</h2>
        <p>
          This Privacy Policy is reviewed annually and updated whenever there are
          significant changes to the law, the services we offer or the way we process
          personal data. Any updates will be published on this page with a revised
          &ldquo;Last updated&rdquo; date at the top.
        </p>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
